/**
 * Dashboard Page
 *
 * Main authenticated area with tabbed navigation for:
 * - Blog Generation
 * - Knowledge Vault (RAG document management)
 * - Brand Profiles
 * - History
 * - Settings
 *
 * Uses mobile-optimized layout on native apps (iOS/Android).
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  User,
  Settings,
  LogOut,
  Shield,
  ChevronDown,
  Menu,
  X,
  FileText,
  History,
  CreditCard,
  Database,
  Briefcase,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BlogGenerator } from '../components/BlogGenerator';
import { KnowledgeVault } from '../components/KnowledgeVault';
import { BrandProfiles } from '../components/BrandProfiles';
import MFAEnrollment from '../components/auth/MFAEnrollment';
import { MobileDashboard } from '../components/mobile';
import { Platform } from '../utils/platform';

type TabId = 'generate' | 'knowledge' | 'brands' | 'history' | 'billing' | 'settings';

interface TabConfig {
  id: TabId;
  name: string;
  icon: React.ElementType;
  path: string;
}

const TABS: TabConfig[] = [
  { id: 'generate', name: 'Generate', icon: FileText, path: '/dashboard' },
  { id: 'knowledge', name: 'Knowledge', icon: Database, path: '/dashboard/knowledge' },
  { id: 'brands', name: 'Brands', icon: Briefcase, path: '/dashboard/brands' },
  { id: 'history', name: 'History', icon: History, path: '/dashboard/history' },
  { id: 'billing', name: 'Billing', icon: CreditCard, path: '/dashboard/billing' },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOutUser, getAuthToken } = useAuth();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);

  // Brand profile state (shared across components)
  const [selectedBrandProfileId, setSelectedBrandProfileId] = useState<string>('default');

  // Get the auth token for API calls
  const getApiHeaders = async () => {
    const token = await getAuthToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  // Use mobile layout on native devices
  if (Platform.isNative() || Platform.isMobileBrowser()) {
    return <MobileDashboard getAuthHeaders={getApiHeaders} />;
  }

  // Determine active tab from URL
  const getActiveTab = (): TabId => {
    const path = location.pathname;
    if (path.includes('/knowledge')) return 'knowledge';
    if (path.includes('/brands')) return 'brands';
    if (path.includes('/history')) return 'history';
    if (path.includes('/billing')) return 'billing';
    if (path.includes('/settings')) return 'settings';
    return 'generate';
  };

  const [activeTab, setActiveTab] = useState<TabId>(getActiveTab());

  // Sync URL with tab
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  const handleTabChange = (tab: TabConfig) => {
    setActiveTab(tab.id);
    navigate(tab.path);
    setShowMobileMenu(false);
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Check if user should be prompted to enable MFA
  const shouldPromptMFA = user && !user.mfaEnabled;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'generate':
        return (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">0</p>
                    <p className="text-sm text-gray-500">Posts Generated</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <BookOpen className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">0</p>
                    <p className="text-sm text-gray-500">Words Written</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Database className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">0</p>
                    <p className="text-sm text-gray-500">Knowledge Docs</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Blog Generator */}
            <BlogGenerator getAuthHeaders={getApiHeaders} />
          </div>
        );

      case 'knowledge':
        return (
          <KnowledgeVault
            brandProfileId={selectedBrandProfileId}
            getAuthHeaders={getApiHeaders}
          />
        );

      case 'brands':
        return (
          <BrandProfiles
            getAuthHeaders={getApiHeaders}
            selectedProfileId={selectedBrandProfileId}
            onProfileSelect={setSelectedBrandProfileId}
          />
        );

      case 'history':
        return (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center">
            <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Generation History
            </h3>
            <p className="text-gray-500 mb-6">
              View your previously generated blog posts and their status
            </p>
            <p className="text-sm text-gray-400">
              No posts generated yet. Start by creating your first blog post!
            </p>
          </div>
        );

      case 'billing':
        return (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CreditCard className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Billing & Subscription</h2>
                  <p className="text-sm text-gray-500">
                    Manage your plan and payment methods
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              {/* Current Plan */}
              <div className="p-4 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl text-white mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Current Plan</p>
                    <p className="text-2xl font-bold">Free Trial</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm opacity-90">Posts Remaining</p>
                    <p className="text-2xl font-bold">3 / 3</p>
                  </div>
                </div>
              </div>

              {/* Upgrade Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    name: 'Starter',
                    price: '$29',
                    posts: '10 credits/mo',
                    features: ['SEO Optimization', 'Basic Analytics'],
                  },
                  {
                    name: 'Pro',
                    price: '$79',
                    posts: '50 credits/mo',
                    features: ['Knowledge Vault', 'Priority Support', 'Advanced SEO'],
                    popular: true,
                  },
                  {
                    name: 'Agency',
                    price: '$199',
                    posts: '150 credits/mo',
                    features: ['Custom Integrations', 'Dedicated Support', 'API Access'],
                  },
                ].map((plan) => (
                  <div
                    key={plan.name}
                    className={`p-4 rounded-xl border ${
                      plan.popular
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    {plan.popular && (
                      <span className="text-xs px-2 py-1 bg-purple-500 text-white rounded-full">
                        Most Popular
                      </span>
                    )}
                    <h3 className="text-lg font-bold mt-2">{plan.name}</h3>
                    <p className="text-3xl font-bold text-gray-900">
                      {plan.price}
                      <span className="text-sm font-normal text-gray-500">/mo</span>
                    </p>
                    <p className="text-sm text-gray-500 mb-4">{plan.posts}</p>
                    <ul className="text-sm text-gray-600 space-y-2">
                      {plan.features.map((f) => (
                        <li key={f}>• {f}</li>
                      ))}
                    </ul>
                    <button
                      className={`w-full mt-4 py-2 rounded-lg font-medium transition ${
                        plan.popular
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-white border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      Upgrade
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Settings</h2>
            <div className="space-y-6">
              {/* Security Section */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  Security
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Two-Factor Authentication</p>
                    <p className="text-sm text-gray-500">
                      {user?.mfaEnabled
                        ? 'Your account is protected with 2FA'
                        : 'Add an extra layer of security'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowMFASetup(true)}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      user?.mfaEnabled
                        ? 'bg-green-100 text-green-700'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {user?.mfaEnabled ? 'Enabled' : 'Enable 2FA'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-8">
              <Link to="/dashboard" className="flex items-center gap-2">
                <Sparkles className="w-8 h-8 text-purple-600" />
                <span className="text-xl font-bold text-gray-900 hidden sm:block">
                  ContentSage
                </span>
              </Link>

              {/* Desktop Nav Links */}
              <div className="hidden md:flex items-center gap-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === tab.id
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              {/* MFA Prompt */}
              {shouldPromptMFA && !showMFASetup && (
                <button
                  onClick={() => setShowMFASetup(true)}
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-sm font-medium hover:bg-yellow-100 transition"
                >
                  <Shield className="w-4 h-4" />
                  Enable 2FA
                </button>
              )}

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition"
                >
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || 'User'}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-purple-600" />
                    </div>
                  )}
                  <span className="hidden sm:block text-sm font-medium text-gray-700">
                    {user?.displayName || user?.email?.split('@')[0]}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {/* User Dropdown */}
                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50"
                    >
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.displayName}
                        </p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>

                      <div className="py-1">
                        <button
                          onClick={() => {
                            handleTabChange({ id: 'settings', name: 'Settings', icon: Settings, path: '/dashboard/settings' });
                            setShowUserMenu(false);
                          }}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                        >
                          <Settings className="w-4 h-4" />
                          Settings
                        </button>

                        {!user?.mfaEnabled && (
                          <button
                            onClick={() => {
                              setShowMFASetup(true);
                              setShowUserMenu(false);
                            }}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                          >
                            <Shield className="w-4 h-4" />
                            Enable Two-Factor Auth
                          </button>
                        )}
                      </div>

                      <div className="border-t border-gray-100 pt-1">
                        <button
                          onClick={handleSignOut}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition"
              >
                {showMobileMenu ? (
                  <X className="w-6 h-6 text-gray-600" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-200 bg-white"
            >
              <div className="px-4 py-2 space-y-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium w-full text-left ${
                      activeTab === tab.id
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.name}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* MFA Setup Modal */}
      <AnimatePresence>
        {showMFASetup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowMFASetup(false);
            }}
          >
            <MFAEnrollment
              onComplete={() => setShowMFASetup(false)}
              onSkip={() => setShowMFASetup(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner - Only on Generate tab */}
        {activeTab === 'generate' && (
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.displayName?.split(' ')[0] || 'there'}!
            </h1>
            <p className="text-gray-600 mt-1">
              Create SEO-optimized blog posts with AI
            </p>
          </div>
        )}

        {/* Tab Content */}
        {renderTabContent()}
      </main>
    </div>
  );
}
